import { StyleSheet, type TextStyle } from 'react-native';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';

export const posCartLinesStyleDefs = {
  cartRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },

  cartRowLast: {
      borderBottomWidth: 0,
    },

  cartCanvasLine: {
      gap: 6,
      paddingBottom: spacing.sm,
      marginBottom: spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },

  cartCanvasLineCompact: {
      gap: 3,
      paddingBottom: 6,
      marginBottom: 6,
    },

  cartCanvasLineDense: {
      gap: 4,
      paddingBottom: spacing.xs,
      marginBottom: spacing.xs,
    },

  cartTicketLine: {
      gap: 0,
      paddingVertical: 4,
      paddingHorizontal: 0,
      marginBottom: 0,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.borderSoft,
      minHeight: 40,
      justifyContent: 'center',
    },

  cartTicketLineActive: {
      backgroundColor: ticketColors.brandSoft,
      marginHorizontal: -8,
      paddingHorizontal: 8,
      paddingVertical: 8,
      borderRadius: 10,
      borderBottomColor: 'transparent',
      minHeight: 0,
    },

  cartTicketRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      minWidth: 0,
    },

  cartTicketQty: {
      width: 28,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: '600',
      color: colors.textSecondary,
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
      fontFamily: ticketFonts.mono,
    },

  cartTicketNameCol: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },

  /** Hit target for expand/collapse on mobile sheet — sibling of price/hint, not their parent. */
  cartTicketSelectHit: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },

  cartTicketName: {
      fontSize: 13,
      lineHeight: 16,
      fontWeight: '500',
      color: colors.textPrimary,
      letterSpacing: -0.1,
      fontFamily: ticketFonts.sans,
    },

  cartTicketMods: {
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '500',
      color: colors.textSecondary,
      fontFamily: ticketFonts.sans,
    },

  cartTicketNote: {
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '500',
      fontStyle: 'italic',
      color: colors.accent,
      fontFamily: ticketFonts.sans,
    },

  cartTicketPrice: {
      fontSize: 13,
      lineHeight: 16,
      fontWeight: '600',
      color: colors.textPrimary,
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
      letterSpacing: -0.2,
      textAlign: 'right',
      fontFamily: ticketFonts.mono,
    },

  cartTicketEndCol: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
    },

  cartTicketPriceCol: {
      alignItems: 'flex-end',
      justifyContent: 'center',
    },

  cartTicketListPrice: {
      fontSize: 10,
      lineHeight: 12,
      fontWeight: '500',
      color: colors.textMuted,
      textDecorationLine: 'line-through',
      textAlign: 'right',
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
      fontFamily: ticketFonts.mono,
    },

  cartTicketEditRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      marginTop: 6,
      paddingLeft: 0,
  },

  cartTicketStepper: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.background,
      overflow: 'hidden',
      flexShrink: 0,
    },

  cartTicketStepBtn: {
      width: 34,
      height: 34,
      alignItems: 'center',
      justifyContent: 'center',
    },

  cartTicketStepLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.textSecondary,
    },

  cartTicketQtyInput: {
      minWidth: 42,
      maxWidth: 48,
      height: 34,
      paddingVertical: 0,
      paddingHorizontal: 4,
      borderWidth: 0,
      borderLeftWidth: 1,
      borderRightWidth: 1,
      borderColor: colors.border,
      borderRadius: 0,
      backgroundColor: 'transparent',
      color: colors.textPrimary,
      textAlign: 'center',
      fontSize: 14,
      fontWeight: '700',
    },

  cartTicketRemoveBtn: {
      paddingVertical: 8,
      paddingHorizontal: 2,
      flexShrink: 0,
    },

  cartTicketRemoveText: {
      ...typography.caption,
      color: colors.textMuted,
      fontWeight: '500',
      fontSize: 12,
    },

  cartLineTopRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: spacing.sm,
      minWidth: 0,
    },

  cartLineNameBlock: {
      flex: 1,
      minWidth: 0,
      gap: 1,
    },

  cartLineBottomRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.xs,
      minWidth: 0,
    },

  cartLineSignalsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      flexShrink: 0,
    },

  cartCanvasLineWideRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      minHeight: 44,
    },

  cartCanvasLineMobileTop: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: spacing.sm,
      minWidth: 0,
    },

  cartCanvasLineMobileBottom: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      marginTop: 4,
      paddingLeft: 20,
    },

  cartLineStockHintSlot: {
      width: 22,
      height: 22,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },

  cartAttributionBtn: {
      width: 26,
      height: 26,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginLeft: 2,
    },

  cartCanvasLineInlineControls: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
    },

  cartActionButtonWide: {
      width: 36,
      height: 36,
      borderRadius: 10,
    },

  cartTrashButtonWide: {
      width: 36,
      height: 36,
      borderRadius: 10,
    },

  cartQuantityInputWide: {
      minWidth: 40,
      maxWidth: 46,
      minHeight: 36,
      paddingVertical: 6,
    },

  cartCanvasControls: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 4,
    },

  cartCanvasControlsCompact: {
      gap: 3,
    },

  cartLineIndex: {
      ...typography.caption,
      color: colors.textMuted,
      fontWeight: '700',
      width: 22,
      textAlign: 'right',
      paddingTop: 2,
    },

  cartNameCompact: {
      fontSize: 14,
      lineHeight: 18,
      fontWeight: '700',
      color: colors.textPrimary,
    },

  cartNameCompactDense: {
      fontSize: 13,
      lineHeight: 16,
    },

  cartPriceMeta: {
      ...typography.caption,
      fontSize: 12,
      lineHeight: 15,
      color: colors.textSecondary,
      fontWeight: '600',
    },

  cartPriceMetaDense: {
      fontSize: 11,
      lineHeight: 13,
    },

  cartQuantityInput: {
      minWidth: 36,
      maxWidth: 42,
      paddingVertical: 3,
      paddingHorizontal: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.inputBorder,
      backgroundColor: colors.inputBackground,
      color: colors.textPrimary,
      textAlign: 'center',
      fontSize: 14,
      fontWeight: '700',
    },

  cartQuantityInputCompact: {
      minWidth: 32,
      maxWidth: 36,
      paddingVertical: 1,
      paddingHorizontal: 4,
      borderRadius: 7,
      fontSize: 13,
    },

  /** Aviso bajo el input de cantidad cuando el usuario escribe un separador
   *  decimal en una línea de unidades enteras (el sanitizador lo descarta). */
  cartQtyIntegerHint: {
      marginTop: 3,
      fontSize: 11,
      lineHeight: 14,
      color: ticketColors.warn,
      fontFamily: ticketFonts.sans,
    },

  cartTrashButton: {
      width: 28,
      height: 28,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.controlBorder,
      backgroundColor: colors.controlSurface,
      justifyContent: 'center',
      alignItems: 'center',
    },

  cartTrashButtonCompact: {
      width: 25,
      height: 25,
      borderRadius: 7,
    },

  cartLineSubtotal: {
      ...typography.caption,
      color: colors.textPrimary,
      fontWeight: '800',
      lineHeight: 14,
    },

  cartLineSubtotalCompact: {
      fontSize: 12,
      lineHeight: 13,
    },

  cartLinePriceColumn: {
      alignItems: 'flex-end',
      justifyContent: 'center',
      minWidth: 72,
      flexShrink: 0,
      gap: 1,
    },

  cartLineListPrice: {
      ...typography.caption,
      color: colors.textSecondary,
      fontWeight: '600',
      textDecorationLine: 'line-through',
      fontVariant: ['tabular-nums'] as TextStyle['fontVariant'],
    },

  cartLineListPriceCompact: {
      fontSize: 10,
      lineHeight: 12,
    },

  cartLineNetPrice: {
      color: colors.textPrimary,
    },

  cartLinePromoHint: {
      ...typography.caption,
      color: colors.success,
      fontWeight: '700',
      fontSize: 11,
      lineHeight: 13,
    },

  cartLinePromoHintCompact: {
      fontSize: 10,
      lineHeight: 12,
    },

  cartLineNameRow: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      minWidth: 0,
    },

  cartLineNameText: {
      flex: 1,
      minWidth: 0,
    },

  cartLineMetaText: {
      flexShrink: 1,
      minWidth: 0,
    },

  cartLineMods: {
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '500',
      color: colors.textSecondary,
    },

  cartLineNote: {
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '500',
      fontStyle: 'italic',
      color: colors.accent,
    },

  cartLinePromoRow: {
      paddingLeft: 20,
      marginTop: 3,
    },

  cartActionButton: {
      width: 28,
      height: 28,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.controlBorder,
      backgroundColor: colors.controlSurface,
      justifyContent: 'center',
      alignItems: 'center',
    },

  cartActionButtonCompact: {
      width: 25,
      height: 25,
      borderRadius: 7,
    },

  cartActionLabel: {
      fontSize: 16,
      fontWeight: '700',
      color: colors.textPrimary,
    },

  cartActionLabelCompact: {
      fontSize: 14,
      lineHeight: 16,
    },
} as const;
